import React, { useEffect } from 'react';

// -- Inline SVG icons ------------------------------------------------------

const UploadIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const ListIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <circle cx="4" cy="6" r="1.5" />
    <circle cx="4" cy="12" r="1.5" />
    <circle cx="4" cy="18" r="1.5" />
  </svg>
);

const RefreshIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
  </svg>
);

const ChartIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 17 9 11 13 15 21 7" />
    <polyline points="14 7 21 7 21 14" />
  </svg>
);

const CloseIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// -- One step row ----------------------------------------------------------

const Step = ({ icon, title, children }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      <p className="mt-1 text-sm text-slate-600 leading-relaxed">{children}</p>
    </div>
  </div>
);

// -- Modal -----------------------------------------------------------------

const HowItWorksModal = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    const handler = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    // Stop body scrolling while modal is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="how-it-works-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between rounded-t-2xl">
          <div className="pr-4">
            <h2 id="how-it-works-title" className="text-xl font-bold text-slate-900">
              How BackflowOffice Works
            </h2>
            <p className="mt-0.5 text-sm text-slate-500">
              A simple workflow for following up on every past-due invoice, open quote, and upcoming test.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 text-slate-400 hover:text-slate-700 rounded-lg p-1 hover:bg-slate-100 transition-colors"
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          <Step icon={<UploadIcon />} title="1. Upload your BSI export">
            Open the{' '}
            <span className="font-semibold">Past Due Invoices</span>,{' '}
            <span className="font-semibold">Quotes Update</span>, or{' '}
            <span className="font-semibold">Upcoming Tests Update</span> page and upload your latest export from BSI.
            The system imports the data and automatically removes anything that's been resolved — paid invoices,
            closed quotes, scheduled tests — so you only see what still needs work.
          </Step>

          <Step icon={<ListIcon />} title="2. Work the list daily">
            Open{' '}
            <span className="font-semibold">Invoices Management</span>,{' '}
            <span className="font-semibold">Quotes Management</span>, or{' '}
            <span className="font-semibold">Upcoming Tests</span>. Sort by Action Date to see what's due today.
            To work an item,{' '}
            <span className="font-semibold">double-click its invoice number</span>{' '}
            (or quote / upcoming test) to open the note. Log what happened — we recommend{' '}
            <span className="font-semibold">starting each note with your initials</span>{' '}
            (e.g. <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">DY: LVM, will retry Tues</span>)
            so the team can see who worked it — and set the next Action Date. Your notes and dates are preserved
            across future uploads.
          </Step>

          <Step icon={<RefreshIcon />} title="3. Re-upload as new BSI data comes in">
            Whenever you pull a fresh export from BSI — typically weekly — upload it again. Paid invoices and
            closed quotes drop off automatically. New items appear. The notes and action dates you've already
            logged on existing items stay intact.
          </Step>

          <Step icon={<ChartIcon />} title="4. Track results on Business Impact">
            The <span className="font-semibold">Business Impact</span> page shows the owner everything that's
            been resolved over the last 30 and 90 days — invoices collected, quotes closed, tests scheduled,
            and an estimated dollar impact. Share it with ownership any time.
          </Step>

          <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3">
            <p className="text-sm text-blue-900">
              <span className="font-semibold">Recommended order:</span> Past Due Invoices first (most urgent
              revenue), then Quotes (pipeline), then Upcoming Tests (recurring revenue).
            </p>
          </div>

          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3">
            <p className="text-sm text-emerald-900">
              <span className="font-semibold">Tip:</span> Use the Preview step on the upload pages to confirm
              your data looks right before committing the changes.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 px-6 py-3 rounded-b-2xl flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default HowItWorksModal;
